import React, { useMemo } from 'react'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { TextField } from '@material-ui/core'
import { FieldNames } from '../../../types/publishSchedule'
import { setFieldValue } from '../../../actions/publishSchedule'
import {
  getMinEndDate,
  dateTimeFieldLabels,
  dateTimeFieldPlaceholders,
} from '../../../services/publishSchedule'
import { DATE_FORMATS } from '../../../config'
import { StyledDatePickerWrapper, StyledIcon } from '../styled'
import { usePublishSchedule } from '../hooks'

export interface IDateTime {
  fieldName:
    | FieldNames.startTime
    | FieldNames.endTime
    | FieldNames.intradayRepeatUntil
  showTimeSelectOnly?: boolean
  dateFormat?: string
}

export const DateTime = ({
  fieldName,
  showTimeSelectOnly = false,
  dateFormat = DATE_FORMATS.DATEPICKER_DEFAULT,
}: IDateTime) => {
  const [{ fieldValues, fieldErrors }, dispatch] = usePublishSchedule()
  const minEndDate = useMemo((): Date => getMinEndDate(fieldValues), [
    fieldValues,
  ])
  const minTime = useMemo((): Date => {
    switch (fieldName) {
      case FieldNames.startTime:
        const paddedCurrentDate = moment()
          .startOf('minute')
          .add(1, 'minute')
          .toDate()
        return moment().isSame(fieldValues.startTime || paddedCurrentDate, 'd')
          ? paddedCurrentDate
          : moment().startOf('day').toDate()
      case FieldNames.endTime:
        return !fieldValues.endTime ||
          moment(minEndDate).isAfter(fieldValues.endTime, 'day')
          ? moment().endOf('day').toDate()
          : moment(fieldValues.endTime || minEndDate).isSame(minEndDate, 'day')
          ? minEndDate
          : moment().startOf('day').toDate()
      case FieldNames.intradayRepeatUntil:
        return moment(fieldValues.startTime || new Date())
          .startOf('minute')
          .add(1, 'minute')
          .toDate()
      default:
        return moment().startOf('day').toDate()
    }
  }, [fieldName, fieldValues.endTime, fieldValues.startTime, minEndDate])

  return (
    <StyledDatePickerWrapper showTopMargin={fieldName !== FieldNames.startTime}>
      <DatePicker
        id={`schedule-${fieldName}-field`}
        fixedHeight
        dateFormat={dateFormat}
        timeFormat="HH:mm"
        placeholderText={dateTimeFieldPlaceholders[fieldName]}
        showTimeSelect
        showTimeSelectOnly={showTimeSelectOnly}
        selected={fieldValues[fieldName]}
        minDate={fieldName === FieldNames.startTime ? minTime : minEndDate}
        maxDate={moment().set('year', 9999).endOf('year').toDate()}
        minTime={minTime}
        maxTime={moment().endOf('day').toDate()}
        timeIntervals={5}
        onChange={(selectedDate: Date | null) => {
          if (
            fieldName === FieldNames.endTime &&
            selectedDate !== null &&
            moment(minEndDate).isAfter(selectedDate)
          ) {
            dispatch(
              setFieldValue(
                fieldName,
                moment(minEndDate)
                  .set('minute', moment(selectedDate).minutes())
                  .toDate()
              )
            )
            return
          }
          dispatch(setFieldValue(fieldName, selectedDate))
        }}
        customInput={
          <TextField
            fullWidth
            label={dateTimeFieldLabels[fieldName]}
            error={!!fieldErrors[fieldName]}
            helperText={fieldErrors[fieldName]}
            InputProps={{
              endAdornment: <StyledIcon />,
            }}
          />
        }
        popperModifiers={{
          offset: {
            enabled: true,
          },
        }}
      />
    </StyledDatePickerWrapper>
  )
}
